import {
  Component, Input, Output, EventEmitter,
  OnInit, OnDestroy, NgZone, ChangeDetectorRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatMessage, ToolUseEvent } from '../../models/chat.models';
import { ChatStateService } from '../../store/chat-state.service';
import { ChatApiService } from '../../services/chat-api.service';
import { ChatConfigService } from '../../services/chat-config.service';

export interface FeedbackEvent  { msgId: string; verdict: 'positive'|'negative'; correction?: string; }
export interface ToolToggleEvent { msgId: string; toolId: string; }

@Component({
  selector: 'app-chat-message',
  template: `

    <!-- ══ System / compact-summary card ══ -->
    <ng-container *ngIf="msg.role==='system'; else normalMsg">
      <div class="xc-compact-card">
        <!-- Header -->
        <div class="xc-compact-hdr">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"
               stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
               width="13" height="13">
            <rect x="1" y="3" width="14" height="10" rx="2"/>
            <line x1="4" y1="7" x2="12" y2="7"/>
            <line x1="5.5" y1="9.5" x2="10.5" y2="9.5"/>
          </svg>
          <span class="xc-compact-label">Compact Summary</span>
          <span class="xc-compact-fold" *ngIf="foldedCount > 0">{{ foldedCount }} messages folded</span>
          <span class="xc-compact-time">{{ msg.timestamp | date:'h:mm a' }}</span>
        </div>
        <!-- Body — rendered markdown, strip [compact] prefix -->
        <div class="xc-compact-body xc-md" [innerHTML]="compactBody | chatMarkdown"></div>
      </div>
    </ng-container>

    <!-- ══ Normal user / assistant message ══ -->
    <ng-template #normalMsg>
    <div class="xc-msg" [class.xc-msg--user]="msg.role==='user'" [class.xc-msg--ai]="msg.role==='assistant'">

      <!-- Avatar -->
      <div class="xc-avatar" [class.xc-avatar--ai]="msg.role==='assistant'">
        <ng-container *ngIf="msg.role==='assistant'; else userAvatar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="15" height="15">
            <path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21a48.25 48.25 0 01-8.135-.687c-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"/>
          </svg>
        </ng-container>
        <ng-template #userAvatar>
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
            <path fill-rule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clip-rule="evenodd"/>
          </svg>
        </ng-template>
      </div>

      <div class="xc-bubble-wrap">

        <!-- Subtle "thinking" pill shown only while tools are actively running -->
        <div class="xc-thinking"
             *ngIf="msg.isStreaming && msg.toolUses && hasRunningTools(msg.toolUses)">
          <span class="xc-thinking-dot"></span>
          <span class="xc-thinking-dot"></span>
          <span class="xc-thinking-dot"></span>
          <span class="xc-thinking-label">Thinking…</span>
        </div>

        <!-- Bubble -->
        <div class="xc-bubble" [class.xc-bubble--error]="!!msg.error">

          <!-- Typing indicator: streaming, no text yet, no tools -->
          <app-chat-typing *ngIf="msg.isStreaming && !displayText && !(msg.toolUses && msg.toolUses.length)">
          </app-chat-typing>

          <!-- Error message -->
          <div class="xc-error-msg" *ngIf="msg.error">
            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
            </svg>
            {{ msg.error }}
          </div>

          <!-- Assistant: rendered markdown -->
          <ng-container *ngIf="!msg.error && msg.role==='assistant'">
            <div class="xc-md" *ngIf="displayText" [innerHTML]="displayText | chatMarkdown"></div>
            <span class="xc-cursor" *ngIf="msg.isStreaming">▍</span>
          </ng-container>

          <!-- User: plain text -->
          <div class="xc-user-text" *ngIf="!msg.error && msg.role==='user'">{{ displayText }}</div>
        </div>

        <!-- Action bar (after streaming) -->
        <div class="xc-actions" *ngIf="msg.role==='assistant' && !msg.isStreaming && !msg.error && displayText">
          <button class="xc-act-btn" title="Copy" (click)="copyMsg()">
            <svg *ngIf="!copied" viewBox="0 0 18 18" fill="currentColor" width="13" height="13">
              <path d="M6.5 1.5A1.5 1.5 0 018 0h4.379a1.5 1.5 0 011.06.44l2.122 2.12A1.5 1.5 0 0116 3.622V12a1.5 1.5 0 01-1.5 1.5H12V15a1.5 1.5 0 01-1.5 1.5H3A1.5 1.5 0 011.5 15V5A1.5 1.5 0 013 3.5h2V3A1.5 1.5 0 016.5 1.5z"/>
            </svg>
            <svg *ngIf="copied" viewBox="0 0 18 18" fill="currentColor" width="13" height="13">
              <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"/>
            </svg>
          </button>
          <button class="xc-act-btn" [class.xc-act-btn--active-pos]="msg.feedback==='positive'" title="Good" (click)="onFeedback('positive')">
            <svg viewBox="0 0 18 18" fill="currentColor" width="13" height="13"><path d="M1 8.25a1.25 1.25 0 112.5 0v7.5a1.25 1.25 0 11-2.5 0v-7.5zM11 3V1.7c0-.268.14-.526.395-.607A2 2 0 0114 3c0 .995-.182 1.948-.514 2.826-.204.54.166 1.174.744 1.174h2.52c1.243 0 2.261 1.01 2.146 2.247a23.864 23.864 0 01-1.341 5.974C17.153 16.323 16.072 17 14.9 17h-3.192a3 3 0 01-1.341-.317l-2.734-1.366A3 3 0 006.292 15H5V8h.963c.685 0 1.258-.483 1.612-1.068a4.011 4.011 0 012.166-1.73c.432-.143.853-.386 1.011-.814.16-.432.248-.9.248-1.388z"/></svg>
          </button>
          <button class="xc-act-btn" [class.xc-act-btn--active-neg]="msg.feedback==='negative'" title="Poor" (click)="onFeedback('negative')">
            <svg viewBox="0 0 18 18" fill="currentColor" width="13" height="13"><path d="M17.905 12.75a1.25 1.25 0 11-2.5 0v-7.5a1.25 1.25 0 012.5 0v7.5zM7.905 17v1.3c0 .268-.14.526-.395.607A2 2 0 014.905 17c0-.995.182-1.948.514-2.826.204-.54-.166-1.174-.744-1.174h-2.52c-1.243 0-2.261-1.01-2.146-2.247.193-2.116.76-4.096 1.341-5.974C1.752 3.678 2.833 3 4.005 3h3.192a3 3 0 011.341.317l2.734 1.366A3 3 0 0012.613 5h1.292v7h-.963c-.685 0-1.258.483-1.612 1.068a4.011 4.011 0 01-2.166 1.73c-.432.143-.853.386-1.011.814-.16.432-.248.9-.248 1.388z"/></svg>
          </button>
          <span class="xc-tokens" *ngIf="msg.usage">
            {{ msg.usage.input_tokens + msg.usage.output_tokens | number }} tokens
          </span>
        </div>

        <!-- ── Export bar ── -->
        <div class="xc-export-bar" *ngIf="msg.role==='assistant' && !msg.isStreaming && !msg.error && displayText">
          <span class="xc-export-label">Export this result as:</span>

          <!-- CSV -->
          <ng-container [ngSwitch]="exportState['csv']">
            <button *ngSwitchCase="'idle'"    class="xc-exp-btn" (click)="exportCsv()">
              <svg viewBox="0 0 14 14" fill="currentColor" width="11" height="11"><path d="M2 1.5A1.5 1.5 0 013.5 0h5.379a1.5 1.5 0 011.06.44l2.122 2.12A1.5 1.5 0 0112.5 3.62V12.5A1.5 1.5 0 0111 14H3.5A1.5 1.5 0 012 12.5v-11z"/></svg>
              CSV
            </button>
            <span *ngSwitchCase="'loading'"   class="xc-exp-btn xc-exp-btn--loading">
              <span class="xc-exp-spin"></span>Preparing…
            </span>
            <a    *ngSwitchCase="'done'"       class="xc-exp-btn xc-exp-btn--done" [href]="exportUrls['csv']" [download]="exportFilenames['csv']">
              <svg viewBox="0 0 14 14" fill="currentColor" width="11" height="11"><path fill-rule="evenodd" d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" clip-rule="evenodd"/></svg>
              CSV
            </a>
            <span *ngSwitchCase="'error'"     class="xc-exp-btn xc-exp-btn--error" [title]="exportErrors['csv']">✕ CSV</span>
          </ng-container>

          <!-- PDF -->
          <ng-container [ngSwitch]="exportState['pdf']">
            <button *ngSwitchCase="'idle'"    class="xc-exp-btn" (click)="exportPdf()">
              <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3" width="11" height="11"><rect x="2" y="1" width="10" height="12" rx="1.5"/><line x1="4" y1="4.5" x2="10" y2="4.5"/><line x1="4" y1="7" x2="10" y2="7"/><line x1="4" y1="9.5" x2="7" y2="9.5"/></svg>
              PDF
            </button>
            <span *ngSwitchCase="'loading'"   class="xc-exp-btn xc-exp-btn--loading">
              <span class="xc-exp-spin"></span>Opening…
            </span>
            <span *ngSwitchCase="'done'"      class="xc-exp-btn xc-exp-btn--done">
              <svg viewBox="0 0 14 14" fill="currentColor" width="11" height="11"><path fill-rule="evenodd" d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" clip-rule="evenodd"/></svg>
              Sent to printer
            </span>
            <span *ngSwitchCase="'error'"     class="xc-exp-btn xc-exp-btn--error" [title]="exportErrors['pdf']">✕ PDF</span>
          </ng-container>

          <!-- Chart PNG -->
          <ng-container [ngSwitch]="exportState['chart']">
            <button *ngSwitchCase="'idle'"    class="xc-exp-btn" (click)="exportChart()" [disabled]="!hasChartableData">
              <svg viewBox="0 0 14 14" fill="currentColor" width="11" height="11"><path d="M1 12h12M3 9l2-4 2 2 2-3 2 4"/></svg>
              Chart (PNG)
            </button>
            <span *ngSwitchCase="'loading'"   class="xc-exp-btn xc-exp-btn--loading">
              <span class="xc-exp-spin"></span>Generating…
            </span>
            <a    *ngSwitchCase="'done'"      class="xc-exp-btn xc-exp-btn--done" [href]="exportUrls['chart']" target="_blank" download>
              <svg viewBox="0 0 14 14" fill="currentColor" width="11" height="11"><path d="M7 1v8M4 6l3 3 3-3M2 11h10" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>
              Download PNG
            </a>
            <span *ngSwitchCase="'error'"     class="xc-exp-btn xc-exp-btn--error" [title]="exportErrors['chart']">✕ Chart</span>
          </ng-container>
        </div>

        <!-- Negative feedback correction box -->
        <div class="xc-correction" *ngIf="showCorrection">
          <textarea class="xc-correction-ta" [(ngModel)]="correctionText"
            placeholder="What should the response have been? (optional)" rows="3"></textarea>
          <div class="xc-correction-btns">
            <button class="xc-btn xc-btn--primary" (click)="submitCorrection()">Submit</button>
            <button class="xc-btn xc-btn--ghost" (click)="showCorrection=false">Cancel</button>
          </div>
        </div>

        <div class="xc-time">{{ msg.timestamp | date:'h:mm a' }}</div>
      </div>
    </div>
    </ng-template>
  `,
  styles: [`
    .xc-msg { display:flex;gap:10px;padding:4px 16px;animation:xcIn .22s ease; }
    .xc-msg--user { flex-direction:row-reverse; }
    @keyframes xcIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}

    .xc-avatar{width:30px;height:30px;border-radius:50%;flex-shrink:0;background:#e8edf5;color:#6b7a99;display:flex;align-items:center;justify-content:center;margin-top:2px}
    .xc-avatar--ai{background:linear-gradient(135deg,#5b7fef,#7c3aed);color:#fff}

    .xc-bubble-wrap{max-width:92%;display:flex;flex-direction:column;gap:3px}
    .xc-msg--user .xc-bubble-wrap{max-width:76%;align-items:flex-end}

    .xc-tools{display:flex;flex-direction:column;gap:4px}
    .xc-tool{display:flex;align-items:center;flex-wrap:wrap;gap:5px;background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:5px 9px;font-size:11.5px}
    .xc-tool--running{border-color:#fcd34d;background:#fffbeb}
    .xc-tool--done{border-color:#86efac;background:#f0fdf4}
    .xc-tool-spinner{width:10px;height:10px;border-radius:50%;border:2px solid #fcd34d;border-top-color:#f59e0b;animation:xcSpin .7s linear infinite;display:inline-block}
    @keyframes xcSpin{to{transform:rotate(360deg)}}
    .xc-tool-check{color:#16a34a}
    .xc-tool-err{color:#dc2626}
    .xc-tool-name{font-weight:600;color:#374151;font-family:'Courier New',monospace;font-size:11px}
    .xc-tool-badge{font-size:10px;color:#6b7280;background:#f3f4f6;padding:1px 5px;border-radius:999px}
    .xc-tool-badge--running{background:#fef3c7;color:#b45309}
    .xc-tool-badge--err{background:#fff1f0;color:#dc2626}
    .xc-tool-toggle{margin-left:auto;background:none;border:none;cursor:pointer;color:#9ca3af;font-size:9px;padding:2px 4px}
    .xc-tool-detail{width:100%}
    .xc-tool-pre{margin:3px 0 0;padding:6px 8px;border-radius:6px;font-size:10.5px;white-space:pre-wrap;word-break:break-all}
    .xc-tool-pre--input{background:#f8fafc;color:#374151}
    .xc-tool-pre--result{background:#f0fdf4;color:#15803d}

    /* ── Compact summary card ── */
    .xc-compact-card{margin:8px 14px;border-radius:12px;overflow:hidden;border:1px solid #c7d2fe;animation:xcIn .22s ease}
    .xc-compact-hdr{display:flex;align-items:center;gap:7px;padding:8px 12px;background:linear-gradient(90deg,#eef2ff,#f5f3ff);border-bottom:1px solid #c7d2fe}
    .xc-compact-label{font-size:11.5px;font-weight:700;color:#4f46e5;flex:1}
    .xc-compact-fold{font-size:10.5px;color:#818cf8;background:#fff;border:1px solid #c7d2fe;border-radius:999px;padding:1px 8px;white-space:nowrap}
    .xc-compact-time{font-size:10px;color:#a5b4fc;white-space:nowrap}
    .xc-compact-body{padding:12px 14px;background:#fafbff;font-size:13px;line-height:1.65;color:#2d3a5e}

    .xc-bubble{padding:10px 14px;border-radius:16px;font-size:13.5px;line-height:1.65;word-break:break-word;background:#f1f5fc;color:#1e2d50;min-height:40px}
    .xc-msg--user .xc-bubble{background:linear-gradient(135deg,#4f7ef7,#6366f1);color:#fff;border-bottom-right-radius:4px}
    .xc-msg--ai .xc-bubble{border-bottom-left-radius:4px}
    .xc-bubble--error{background:#fff1f0!important;border:1px solid #fca5a5;color:#991b1b!important}
    .xc-error-msg{display:flex;align-items:center;gap:6px;color:#991b1b;font-size:13px}

    :host ::ng-deep .xc-md{display:block}
    :host ::ng-deep .xc-md .chat-h1{font-size:1.25em;font-weight:700;margin:6px 0 3px}
    :host ::ng-deep .xc-md .chat-h2{font-size:1.1em;font-weight:700;margin:6px 0 3px}
    :host ::ng-deep .xc-md .chat-h3{font-size:1em;font-weight:600;margin:5px 0 2px}
    :host ::ng-deep .xc-md .chat-code-block{background:#1e2235;color:#abb2bf;padding:12px 14px;border-radius:8px;overflow-x:auto;margin:8px 0;font-family:'Courier New',monospace;font-size:12px;line-height:1.6}
    :host ::ng-deep .xc-md .chat-inline-code{background:rgba(0,0,0,.07);padding:1px 5px;border-radius:4px;font-family:'Courier New',monospace;font-size:85%}
    :host ::ng-deep .xc-md .chat-ul{padding-left:18px;margin:4px 0}
    :host ::ng-deep .xc-md li{margin:2px 0}
    :host ::ng-deep .xc-md .chat-link{color:#4f7ef7;text-decoration:underline}
    :host ::ng-deep .xc-md p{margin:3px 0}
    :host ::ng-deep .xc-md .chat-bq{border-left:3px solid #6366f1;padding-left:10px;color:#6b7a99;margin:4px 0;font-style:italic}
    :host ::ng-deep .xc-md .chat-hr{border:none;border-top:1px solid #e2e8f0;margin:8px 0}
    /* ── table ── */
    :host ::ng-deep .xc-md .chat-table-wrap{overflow-x:auto;margin:10px 0;border-radius:10px;border:1px solid #e2e8f0;box-shadow:0 1px 4px rgba(0,0,0,.05)}
    :host ::ng-deep .xc-md .chat-table{border-collapse:collapse;width:100%;font-size:12px;min-width:360px}
    :host ::ng-deep .xc-md .chat-table thead tr{background:linear-gradient(90deg,#f0f4fc,#eef2ff)}
    :host ::ng-deep .xc-md .chat-table th{padding:8px 12px;text-align:left;font-weight:700;color:#2d3a5e;font-size:11.5px;letter-spacing:.02em;white-space:nowrap;border-bottom:2px solid #d0d8f0}
    :host ::ng-deep .xc-md .chat-table td{padding:6px 12px;border-bottom:1px solid #f0f4fc;color:#374151;font-size:12px;vertical-align:middle}
    :host ::ng-deep .xc-md .chat-table tbody tr:last-child td{border-bottom:none}
    :host ::ng-deep .xc-md .chat-table tbody tr:hover td{background:#f8faff}
    :host ::ng-deep .xc-md .chat-table tbody tr:nth-child(even) td{background:#fafbfe}
    :host ::ng-deep .xc-md .chat-table tbody tr:nth-child(even):hover td{background:#f4f6ff}

    .xc-cursor{display:inline-block;color:#4f7ef7;animation:xcCur .8s step-start infinite;margin-left:1px}
    @keyframes xcCur{50%{opacity:0}}
    .xc-user-text{white-space:pre-wrap}

    .xc-actions{display:flex;align-items:center;gap:3px;margin-top:2px}
    .xc-act-btn{background:none;border:none;cursor:pointer;padding:4px 5px;color:#c0cad8;border-radius:5px;display:flex;align-items:center;transition:color .15s,background .15s}
    .xc-act-btn:hover{color:#4f7ef7;background:#f0f4fc}
    .xc-act-btn--active-pos{color:#16a34a!important}
    .xc-act-btn--active-neg{color:#dc2626!important}

    /* ── Export bar ── */
    .xc-export-bar{display:flex;align-items:center;flex-wrap:wrap;gap:6px;margin-top:6px;padding:7px 10px;background:#f8fafc;border:1px solid #e8edf5;border-radius:10px}
    .xc-export-label{font-size:11.5px;color:#6b7a99;font-weight:500;white-space:nowrap;flex-shrink:0}
    .xc-exp-btn{
      display:inline-flex;align-items:center;gap:4px;
      background:#fff;border:1px solid #e2e8f0;border-radius:7px;
      padding:4px 11px;font-size:11.5px;font-weight:500;color:#374151;
      cursor:pointer;white-space:nowrap;text-decoration:none;
      transition:background .12s,border-color .12s,color .12s;
    }
    .xc-exp-btn:hover:not(:disabled){background:#eef2ff;border-color:#c7d2fe;color:#4f46e5}
    .xc-exp-btn:disabled{opacity:.4;cursor:not-allowed}
    .xc-exp-btn--loading{background:#f0f4fc;border-color:#d0d8f0;color:#6b7a99;cursor:default;pointer-events:none}
    .xc-exp-btn--done{background:#f0fdf4;border-color:#86efac;color:#15803d}
    .xc-exp-btn--done:hover{background:#dcfce7!important;border-color:#4ade80!important;color:#15803d!important}
    .xc-exp-btn--error{background:#fff1f0;border-color:#fca5a5;color:#dc2626;cursor:default}
    .xc-exp-spin{
      width:10px;height:10px;border-radius:50%;
      border:1.5px solid #c7d2fe;border-top-color:#6366f1;
      animation:xcExpSpin .7s linear infinite;display:inline-block;
    }
    @keyframes xcExpSpin{to{transform:rotate(360deg)}}
    .xc-tokens{margin-left:auto;font-size:10px;color:#c0cad8;background:#f1f5fc;padding:2px 6px;border-radius:999px}

    .xc-correction{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:10px;margin-top:4px}
    .xc-correction-ta{width:100%;border:1px solid #e2e8f0;border-radius:7px;padding:6px 9px;font-size:12.5px;font-family:inherit;resize:vertical;outline:none}
    .xc-correction-ta:focus{border-color:#4f7ef7}
    .xc-correction-btns{display:flex;gap:7px;margin-top:7px}
    .xc-btn{border:none;border-radius:7px;padding:5px 14px;font-size:12px;cursor:pointer;font-family:inherit}
    .xc-btn--primary{background:#4f7ef7;color:#fff}
    .xc-btn--ghost{background:none;border:1px solid #e2e8f0;color:#6b7a99}

    .xc-time{font-size:10px;color:#c0cad8;padding:0 2px}
    .xc-msg--user .xc-time{text-align:right}

    .xc-thinking{display:flex;align-items:center;gap:3px;padding:8px 12px;background:#f1f5fc;border-radius:12px;margin-bottom:4px;width:fit-content}
    .xc-thinking-label{font-size:11.5px;color:#8b96af;margin-left:3px}
    .xc-thinking-dot{width:5px;height:5px;border-radius:50%;background:#6b7a99;animation:xcThinkPulse 1.2s ease-in-out infinite}
    .xc-thinking-dot:nth-child(2){animation-delay:.2s}
    .xc-thinking-dot:nth-child(3){animation-delay:.4s}
    @keyframes xcThinkPulse{0%,80%,100%{opacity:.25;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}
  `]
})
export class ChatMessageComponent implements OnInit, OnDestroy {
  @Input() message!: ChatMessage;
  @Output() feedback    = new EventEmitter<FeedbackEvent>();
  @Output() toolToggle  = new EventEmitter<ToolToggleEvent>();

  msg!: ChatMessage;
  displayText    = '';
  copied         = false;
  showCorrection = false;
  correctionText = '';

  /**
   * For system (compact) messages — text with the `[compact] ... (N messages folded in):\n\n`
   * header stripped, ready for markdown rendering.
   */
  get compactBody(): string {
    const raw = this.displayText;
    // Strip leading "[compact] Earlier conversation summary (N messages folded in):\n\n"
    return raw.replace(/^\[compact\][^\n]*\n\n?/, '').trim();
  }

  /** Number of messages folded, parsed from the [compact] header line */
  get foldedCount(): number {
    const m = this.displayText.match(/\((\d+)\s+messages? folded/i);
    return m ? +m[1] : 0;
  }

  // ── Export bar state ──────────────────────────────────────────────────────
  exportState:     { [k: string]: 'idle' | 'loading' | 'done' | 'error' } = {
    csv: 'idle', pdf: 'idle', chart: 'idle',
  };
  exportUrls:      { [k: string]: string } = {};
  exportFilenames: { [k: string]: string } = {};
  exportErrors:    { [k: string]: string } = {};

  /** True when the response contains a markdown table with at least one numeric column */
  get hasChartableData(): boolean {
    return this._extractChartData(this.displayText) !== null;
  }

  private sub!: Subscription;

  constructor(
    private chatState: ChatStateService,
    private api:       ChatApiService,
    private config:    ChatConfigService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.msg = { ...this.message };
    this.displayText = this._resolve(this.message);

    this.sub = this.chatState.state$.subscribe(state => {
      const sid = state.activeSessionId;
      if (!sid) return;
      const found = (state.messages[sid] || []).find(m => m.id === this.message.id);
      if (!found) return;

      const newText = this._resolve(found);

      // skip no-op updates
      if (
        this.msg.streamText  === found.streamText  &&
        this.msg.isStreaming === found.isStreaming  &&
        this.msg.toolUses    === found.toolUses     &&
        this.msg.feedback    === found.feedback     &&
        this.msg.error       === found.error        &&
        this.displayText     === newText
      ) return;

      this.zone.run(() => {
        this.msg = found;
        this.displayText = newText;
        this.cdr.detectChanges();
      });
    });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  // ── Export methods ────────────────────────────────────────────────────────

  /** Extract markdown pipe-table rows → CSV string */
  private _extractCsv(text: string): string {
    const lines = text.split('\n').filter(l => l.trim().startsWith('|'));
    if (lines.length < 2) {
      // No table — just return the plain text
      return text;
    }
    // Filter out separator lines (|---|---|)
    const rows = lines.filter(l => !l.replace(/\|/g, '').match(/^[-:\s]+$/));
    return rows.map(row =>
      row.replace(/^\||\|$/g, '').split('|')
         .map(cell => `"${cell.replace(/[*_]/g, '').trim().replace(/"/g, '""')}"`)
         .join(',')
    ).join('\n');
  }

  /** Extract chart-able data: first table with a numeric column */
  private _extractChartData(text: string): any | null {
    const lines = text.split('\n').filter(l => l.trim().startsWith('|'));
    if (lines.length < 3) return null;

    const parseCells = (line: string) =>
      line.replace(/^\||\|$/g, '').split('|').map(c => c.replace(/[*_]/g, '').trim());

    const headers   = parseCells(lines[0]);
    const dataLines = lines.slice(2); // skip separator
    if (!dataLines.length) return null;

    // Find first text column and first numeric column
    const firstRow = parseCells(dataLines[0]);
    let labelCol = -1, valueCol = -1;
    firstRow.forEach((cell, i) => {
      const v = cell.replace(/[^\d.-]/g, '');
      if (valueCol === -1 && v !== '' && !isNaN(parseFloat(v))) valueCol = i;
      else if (labelCol === -1 && isNaN(parseFloat(cell)) && cell) labelCol = i;
    });

    if (valueCol === -1) return null;
    if (labelCol === -1) labelCol = valueCol === 0 ? 1 : 0;

    const values = dataLines.slice(0, 50).map(line => {
      const cells = parseCells(line);
      return {
        x: cells[labelCol] || '',
        y: parseFloat(cells[valueCol]?.replace(/[^\d.-]/g, '') || '0') || 0,
      };
    }).filter(v => v.x);

    if (!values.length) return null;
    return { series: [{ name: headers[valueCol] || 'Value', values }] };
  }

  exportCsv(): void {
    this.exportState['csv'] = 'loading';
    this.cdr.detectChanges();

    try {
      const csv  = this._extractCsv(this.displayText);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const title = (this.chatState.snapshot.sessions.find(
        s => s.id === this.msg.sessionId
      )?.title || 'export').replace(/[^a-z0-9]/gi, '_').slice(0, 40);

      this.exportUrls['csv']      = url;
      this.exportFilenames['csv'] = `${title}.csv`;
      this.exportState['csv']     = 'done';

      // Trigger download immediately
      const a   = document.createElement('a');
      a.href     = url;
      a.download = this.exportFilenames['csv'];
      a.click();
    } catch (e: any) {
      this.exportState['csv']  = 'error';
      this.exportErrors['csv'] = e.message || 'Export failed';
    }
    this.cdr.detectChanges();
  }

  exportPdf(): void {
    this.exportState['pdf'] = 'loading';
    this.cdr.detectChanges();

    try {
      const title = this.chatState.snapshot.sessions.find(
        s => s.id === this.msg.sessionId
      )?.title || 'AI Response';

      // Build a minimal markdown → HTML for the print window
      const bodyHtml = this.displayText
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        // tables
        .replace(/^(\|.+)\n(\|[-| :]+)\n((?:\|.+\n?)+)/gm, (_m, hdr, _sep, body) => {
          const th = hdr.replace(/^\||\|$/g,'').split('|').map((h: string) => `<th>${h.trim()}</th>`).join('');
          const rows = body.trim().split('\n').map((r: string) =>
            '<tr>' + r.replace(/^\||\|$/g,'').split('|').map((c: string) => `<td>${c.trim()}</td>`).join('') + '</tr>'
          ).join('');
          return `<table><thead><tr>${th}</tr></thead><tbody>${rows}</tbody></table>`;
        })
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>');

      const printHtml = `<!DOCTYPE html><html><head>
        <title>${title}</title>
        <style>
          body{font-family:-apple-system,sans-serif;max-width:820px;margin:0 auto;padding:28px;color:#1e2d50}
          h1,h2,h3{color:#1e2d50}
          table{border-collapse:collapse;width:100%;margin:12px 0;font-size:12px}
          th{background:#eef2ff;padding:7px 10px;text-align:left;font-weight:600;border-bottom:2px solid #c7d2fe}
          td{padding:6px 10px;border-bottom:1px solid #f0f4fc}
          tr:nth-child(even) td{background:#fafbff}
          pre{background:#1e2235;color:#abb2bf;padding:12px;border-radius:6px;overflow-x:auto}
          code{background:rgba(0,0,0,.07);padding:1px 5px;border-radius:3px;font-size:85%}
          .footer{color:#94a3b8;font-size:11px;margin-top:24px;border-top:1px solid #e8edf5;padding-top:10px}
          @media print{body{margin:0}}
        </style>
      </head><body>
        <h2 style="color:#6366f1;border-bottom:2px solid #eef2ff;padding-bottom:8px">${title}</h2>
        <p>${bodyHtml}</p>
        <div class="footer">Exported from XEDGE AI Assistant &nbsp;·&nbsp; ${new Date().toLocaleString()}</div>
      </body></html>`;

      const win = window.open('', '_blank', 'width=900,height=720');
      if (win) {
        win.document.write(printHtml);
        win.document.close();
        setTimeout(() => { win.print(); }, 400);
        this.exportState['pdf'] = 'done';
      } else {
        throw new Error('Pop-up blocked — allow pop-ups and try again');
      }
    } catch (e: any) {
      this.exportState['pdf']  = 'error';
      this.exportErrors['pdf'] = e.message || 'Export failed';
    }
    this.cdr.detectChanges();
  }

  exportChart(): void {
    const data = this._extractChartData(this.displayText);
    if (!data) {
      this.exportState['chart']  = 'error';
      this.exportErrors['chart'] = 'No chart-able table found in this response';
      this.cdr.detectChanges();
      return;
    }

    this.exportState['chart'] = 'loading';
    this.cdr.detectChanges();

    const title = this.chatState.snapshot.sessions.find(
      s => s.id === this.msg.sessionId
    )?.title || 'Chart Export';

    this.api.renderChart({
      type:    'bar',
      title,
      data,
      options: { theme: 'light', show_values: true },
      format:  'png',
    }).subscribe({
      next: res => {
        this.exportUrls['chart']  = `${this.config.backendUrl}${res.url}`;
        this.exportState['chart'] = 'done';
        this.cdr.detectChanges();
      },
      error: (e: any) => {
        this.exportState['chart']  = 'error';
        this.exportErrors['chart'] = e.message || 'Chart generation failed';
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Resolve display text from a message in ANY format:
   *   1. Live streaming:   streamText
   *   2. Finalised new:    content[].text
   *   3. Saved session:    (already normalised to content[] in state service)
   */
  private _resolve(m: ChatMessage): string {
    if (m.streamText) return m.streamText;
    if (m.content?.length) {
      const t = m.content.find(c => c.type === 'text');
      if (t?.text) return t.text;
    }
    // fallback: direct text field (shouldn't reach here after normalisation)
    return (m as any).text || '';
  }

  hasRunningTools(tools: ToolUseEvent[]): boolean {
    return tools.some(t => t.status === 'running');
  }

  copyMsg(): void {
    navigator.clipboard?.writeText(this.displayText).then(() => {
      this.zone.run(() => {
        this.copied = true;
        this.cdr.detectChanges();
        setTimeout(() => { this.copied = false; this.cdr.detectChanges(); }, 2000);
      });
    });
  }

  onFeedback(v: 'positive'|'negative'): void {
    if (v === 'negative') this.showCorrection = true;
    else this.feedback.emit({ msgId: this.msg.id, verdict: v });
  }

  submitCorrection(): void {
    this.feedback.emit({ msgId: this.msg.id, verdict: 'negative', correction: this.correctionText });
    this.showCorrection = false;
    this.correctionText = '';
  }
}